import { useCurrentUser } from '@/affiliate-dashboard/context/current-user-context';
import {
  GetViewerDocument,
  OrderType,
  PaymentGateway,
  usePlaceLevelPackageOrderMutation,
} from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { PageHeading } from '@/components/base/page-heading';
import { RadioGroup, RadioGroupItem } from '@/components/base/radio-group';
import Section from '@/components/base/section';
import { Badge } from '@/components/order/badge';
import { RadioItemDecoration } from '@/components/order/radio-item-decoration';
import { AccountPriceDecoration } from '@/monevis-platform/components/order/steps/select-account-size';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from '@tanstack/react-router';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';

const generations = [
  {
    id: 1,
    label: 'Generation 2',
    price: 125,
    level: 2,
  },
  {
    id: 2,
    label: 'Generation 3',
    price: 250,
    level: 3,
  },
  {
    id: 3,
    label: 'Generation 4',
    price: 500,
    level: 4,
  },
  {
    id: 4,
    label: 'Generation 5',
    price: 1000,
    level: 5,
  },
  {
    id: 5,
    label: 'Generation 6',
    price: 2000,
    level: 6,
  },
  {
    id: 6,
    label: 'Generation 7',
    price: 4000,
    level: 7,
  },
  {
    id: 7,
    label: 'Generation 8',
    price: 8000,
    level: 8,
  },
  {
    id: 8,
    label: 'Generation 9',
    price: 16000,
    activated: false,
    level: 9,
  },
  {
    id: 9,
    label: 'Generation 10',
    price: 32000,
    level: 10,
  },
];

export function AffOrder(): JSX.Element {
  const { t } = useTranslation();
  const [selectedItemId, setSelectedItemId] = useState<null | number>(null);
  const [loading, setLoading] = useState(false);
  const intl = useIntl();
  const user = useCurrentUser();
  const [placeLevelPackageOrder] = usePlaceLevelPackageOrderMutation();
  const navigate = useNavigate();

  const userMaxCommissionLevel = user.me.maxCommissionLevel ?? 0;

  async function handleOnChange(value: number): Promise<void> {
    setSelectedItemId(value);
  }

  const currentItem = generations.find((item) => item.id === selectedItemId);

  async function handleSubmit(paymentGateway: PaymentGateway): Promise<void> {
    try {
      setLoading(true);
      if (currentItem) {
        const response = placeLevelPackageOrder({
          variables: {
            orderInput: {
              email: user.me.email,
              amount: currentItem.price,
              currency: 'USD',
              orderType: OrderType.LevelPackage,
              paymentGateway,
              maxCommissionLevel: currentItem?.level,
            },
          },
          refetchQueries: [{ query: GetViewerDocument }],
        });
        navigate({
          to: `/dashboard/upgrade/pay/$order-id`,
          params: {
            'order-id': String(
              (await response).data?.placeLevelPackageOrder.id,
            ),
          },
        });
      }
      setLoading(false);
    } catch (error) {
      enqueueSnackbar('Error while processing payment', {
        variant: 'error',
      });
      setLoading(false);
    }
  }

  return (
    <div>
      <Section variant="secondary">
        <div className="flex w-full flex-col">
          <RadioGroup
            onValueChange={(v: string) => {
              setSelectedItemId(Number(v));
            }}
            className="mb-4 mt-4 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
          >
            {generations.map((item) => {
              return (
                <RadioGroupItem
                  onClick={async () => {
                    await handleOnChange(item.price);
                  }}
                  disabled={item.level <= userMaxCommissionLevel}
                  decoration={
                    <RadioItemDecoration
                      label={
                        item.level <= userMaxCommissionLevel ? (
                          'Activated'
                        ) : (
                          <AccountPriceDecoration
                            price={item.price}
                            finalPrice={item.price}
                            oldPrice={0}
                            currency="USD"
                          />
                        )
                      }
                    />
                  }
                  key={item.id}
                  value={String(item.id)}
                  data-testid={String(item)}
                >
                  <span className="text-xl font-bold">{item.label}</span>
                </RadioGroupItem>
              );
            })}
          </RadioGroup>

          <div className="flex w-full items-center justify-center text-primary-light">
            <a
              href="https://monevis-cdn.s3.eu-central-1.amazonaws.com/Monevis_Affiliate_2025.pdf"
              target="_blank"
              rel="noopener"
            >
              <span>{t('affOrder.openAffiliatePresentations')}</span>
              <FontAwesomeIcon icon={faChevronRight} className="ml-1" />
            </a>
          </div>
        </div>
      </Section>

      <div className="flex flex-col items-center justify-center gap-3">
        <div className="mt-4 flex flex-col items-center">
          <PageHeading size="xxlarge">
            {t('orderSummary.totalToPay')}
          </PageHeading>
          <Badge className="min-w-52" size="lg">
            {intl.formatNumber(currentItem?.price ?? 0, {
              style: 'currency',
              currency: 'USD',
            })}
          </Badge>
        </div>

        <div className="flex flex-col gap-6">
          <div className="mt-4 flex w-full flex-col justify-center gap-2 md:flex-row md:gap-4">
            <div>
              <Button
                loading={loading}
                onClick={() => {
                  handleSubmit(PaymentGateway.Stripe);
                }}
                className="w-full"
                disabled={!selectedItemId}
              >
                {t('affOrder.pay')}
              </Button>
            </div>
            <div>
              <Button
                loading={loading}
                disabled={!selectedItemId}
                onClick={() => {
                  handleSubmit(PaymentGateway.Confirmo);
                }}
                className="w-full"
                variant="outlined"
              >
                {t('affOrder.payWithCryptocurrencies')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
